import { useCallback, useEffect, useRef, useState } from 'react';
export type AIProvider = 'openai' | 'anthropic' | 'mock';
type SuggestionField = 'title' | 'description';
type SuggestionPhase = 'idle' | 'debouncing' | 'streaming' | 'ready' | 'error';
interface StreamEventStatus {
  type: 'status';
  phase: 'streaming' | 'complete';
}
interface StreamEventChunk {
  type: 'chunk';
  chunk: string;
}
interface StreamEventError {
  type: 'error';
  message: string;
}
interface StreamEventDone {
  type: 'done';
}
type SuggestionStreamEvent = StreamEventStatus | StreamEventChunk | StreamEventError | StreamEventDone;
interface UseMapTextSuggestionOptions {
  field: SuggestionField;
  initialValue: string;
  locationExternalId: string;
  mapExternalId?: string | null;
  title?: string;
  provider?: AIProvider;
  model?: string;
  systemPrompt?: string;
  maxTokens?: number;
  authToken?: string | null;
  refreshAuthToken?: (forceRefresh?: boolean) => Promise<string>;
  notifyUnauthorized?: () => void;
  enabled?: boolean;
  debounceMs?: number;
}
interface UseMapTextSuggestionResult {
  phase: SuggestionPhase;
  suggestion: string;
  error: string | null;
  isVisible: boolean;
  handleUserInput: (value: string) => void;
  acceptSuggestion: () => void;
  dismissSuggestion: () => void;
  retrySuggestion: () => void;
}
function parseSsePayload(payload: string): SuggestionStreamEvent | null {
  const normalized = payload.replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return null;
  }
  const data = normalized.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
  if (!data) {
    return null;
  }
  return JSON.parse(data) as SuggestionStreamEvent;
}
async function consumeEventStream(stream: ReadableStream<Uint8Array>, onEvent: (event: SuggestionStreamEvent) => void): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const {
        value,
        done
      } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, {
        stream: true
      });
      let separatorIndex = buffer.indexOf('\n\n');
      while (separatorIndex !== -1) {
        const payload = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        const parsed = parseSsePayload(payload);
        if (parsed) {
          onEvent(parsed);
        }
        separatorIndex = buffer.indexOf('\n\n');
      }
    }
    const remaining = parseSsePayload(buffer);
    if (remaining) {
      onEvent(remaining);
    }
  } finally {
    reader.releaseLock();
  }
}
export function useMapTextSuggestion({
  field,
  initialValue,
  locationExternalId,
  mapExternalId,
  title,
  provider: propsProvider,
  model: propsModel,
  systemPrompt,
  maxTokens,
  authToken,
  refreshAuthToken,
  notifyUnauthorized,
  enabled = true,
  debounceMs = 150
}: UseMapTextSuggestionOptions): UseMapTextSuggestionResult {
  const workerUrl = process.env.NEXT_PUBLIC_AI_STREAM_URL?.replace(/\/$/, '');
  const envProvider = process.env.NEXT_PUBLIC_AI_STREAM_PROVIDER as AIProvider | undefined;
  const envModel = process.env.NEXT_PUBLIC_AI_STREAM_MODEL;
  const provider = propsProvider ?? envProvider;
  const model = propsModel ?? envModel;
  const [phase, setPhase] = useState<SuggestionPhase>('idle');
  const [suggestion, setSuggestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [userAuthoredValue, setUserAuthoredValue] = useState(initialValue);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [requestNonce, setRequestNonce] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastRequestedValueRef = useRef<string | null>(null);
  const dismissedValueRef = useRef<string | null>(null);
  const suggestionRef = useRef('');
  const activeRequestIdRef = useRef(0);
  const clearRequest = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);
  const getAuthorizationHeader = useCallback(async (forceRefresh: boolean = false): Promise<string | null> => {
    if (!forceRefresh && authToken) {
      return `Bearer ${authToken}`;
    }
    if (!refreshAuthToken) {
      return null;
    }
    const nextToken = await refreshAuthToken(forceRefresh);
    return `Bearer ${nextToken}`;
  }, [authToken, refreshAuthToken]);
  useEffect(() => {
    clearRequest();
    setPhase('idle');
    setSuggestion('');
    setError(null);
    setUserAuthoredValue(initialValue);
    setHasUserEdited(false);
    suggestionRef.current = '';
    lastRequestedValueRef.current = null;
    dismissedValueRef.current = null;
    activeRequestIdRef.current = 0;
  }, [clearRequest, initialValue]);
  useEffect(() => {
    return () => {
      clearRequest();
    };
  }, [clearRequest]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: requestNonce is intentionally included as a retry trigger
  useEffect(() => {
    if (!enabled || !workerUrl) {
      setPhase('idle');
      return;
    }
    if (!hasUserEdited) {
      setPhase('idle');
      return;
    }
    const trimmedValue = userAuthoredValue.trim();
    if (!trimmedValue) {
      clearRequest();
      setPhase('idle');
      setSuggestion('');
      setError(null);
      suggestionRef.current = '';
      return;
    }
    const effectiveDismissedValue = field === 'description' ? `${userAuthoredValue}::${title ?? ''}` : userAuthoredValue;
    if (dismissedValueRef.current === effectiveDismissedValue || lastRequestedValueRef.current === userAuthoredValue) {
      return;
    }
    setPhase('debouncing');
    const timeoutId = window.setTimeout(async () => {
      clearRequest();
      const abortController = new AbortController();
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;
      abortControllerRef.current = abortController;
      setPhase('streaming');
      setSuggestion('');
      setError(null);
      suggestionRef.current = '';
      lastRequestedValueRef.current = userAuthoredValue;
      try {
        const requestBody = JSON.stringify({
          input: userAuthoredValue,
          provider,
          model,
          systemPrompt,
          maxTokens
        });
        const sendRequest = async (forceRefreshAuth: boolean = false) => {
          const authorization = await getAuthorizationHeader(forceRefreshAuth);
          if (!authorization) {
            throw new Error('Suggestion auth unavailable');
          }
          return fetch(`${workerUrl}/v1/ai/stream/text`, {
            method: 'POST',
            headers: {
              Authorization: authorization,
              'Content-Type': 'application/json'
            },
            body: requestBody,
            signal: abortController.signal
          });
        };
        let response = await sendRequest();
        if (response.status === 401) {
          response = await sendRequest(true);
        }
        if (response.status === 401) {
          notifyUnauthorized?.();
        }
        if (!response.ok || !response.body) {
          throw new Error(`Suggestion request failed (${response.status})`);
        }
        await consumeEventStream(response.body, event => {
          if (activeRequestIdRef.current !== requestId || abortController.signal.aborted) {
            return;
          }
          if (event.type === 'chunk') {
            suggestionRef.current += event.chunk;
            setSuggestion(suggestionRef.current);
            return;
          }
          if (event.type === 'error') {
            setPhase('error');
            setError(event.message);
            return;
          }
          if (event.type === 'done') {
            setPhase(suggestionRef.current ? 'ready' : 'idle');
          }
        });
      } catch (requestError) {
        if (abortController.signal.aborted || activeRequestIdRef.current !== requestId) {
          return;
        }
        setPhase('error');
        setError(requestError instanceof Error ? requestError.message : 'Failed to fetch suggestion');
      }
    }, debounceMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [clearRequest, debounceMs, enabled, field, hasUserEdited, locationExternalId, mapExternalId, maxTokens, model, provider, getAuthorizationHeader, notifyUnauthorized, systemPrompt, requestNonce, title, userAuthoredValue, workerUrl]);
  const handleUserInput = useCallback((value: string) => {
    dismissedValueRef.current = null;
    setHasUserEdited(true);
    setUserAuthoredValue(value);
    setError(null);
  }, []);
  const acceptSuggestion = useCallback(() => {
    clearRequest();
    activeRequestIdRef.current += 1;
    setPhase('idle');
    setSuggestion('');
    setError(null);
    suggestionRef.current = '';
  }, [clearRequest]);
  const dismissSuggestion = useCallback(() => {
    const cacheKey = field === 'description' ? `${userAuthoredValue}::${title ?? ''}` : userAuthoredValue;
    dismissedValueRef.current = cacheKey;
    clearRequest();
    activeRequestIdRef.current += 1;
    setPhase('idle');
    setSuggestion('');
    setError(null);
    suggestionRef.current = '';
  }, [clearRequest, field, title, userAuthoredValue]);
  const retrySuggestion = useCallback(() => {
    dismissedValueRef.current = null;
    lastRequestedValueRef.current = null;
    setError(null);
    setRequestNonce(current => current + 1);
  }, []);
  return {
    phase,
    suggestion,
    error,
    isVisible: phase !== 'idle' || suggestion.length > 0 || error !== null,
    handleUserInput,
    acceptSuggestion,
    dismissSuggestion,
    retrySuggestion
  };
}